import { createContext, useContext, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useInitiative, type UseInitiativeReturn } from '../hooks/useInitiative';
import { useSessionContext } from './SessionContext';

/**
 * The initiative tracker (brief docs/backlog/initiative-brief.md, 25.10). The state is the server's; every action is a
 * command (no local «roll everybody» / «move the turn» any more — the server owns the order, the rules and the rights).
 */
export interface InitiativeActions {
  start: (tokenIds: string[], opts: { playersRoll: boolean; groupSame: boolean }) => void;
  begin: () => void;
  roll: (entryId: string) => void;
  setValue: (entryId: string, value: number, tokenId?: string) => void;
  add: (tokenIds: string[], joinGroups: boolean) => void;
  remove: (tokenIds: string[]) => void;
  move: (entryId: string, beforeId: string | null) => void;
  /** «Следующий» (GM) / «Закончить ход» (the player whose turn it is). Sends the turn it saw. */
  next: () => void;
  prev: () => void;
  delay: () => void;
  join: (entryId: string) => void;
  split: (entryId: string) => void;
  defeat: (tokenId: string, out: boolean) => void;
  dismissNew: (tokenIds: string[]) => void;
  end: () => void;
  undoLast: (undoId: string) => void;
}

export type InitiativeContextState = UseInitiativeReturn & InitiativeActions;

export interface InitiativeContextValue {
  initiative: InitiativeContextState;
}

const InitiativeContext = createContext<InitiativeContextValue | null>(null);

export function InitiativeProvider({ children }: { children: ReactNode }) {
  const { mp } = useSessionContext();
  const initiative = useInitiative();

  const stateRef = useRef(initiative);
  stateRef.current = initiative;
  const mpRef = useRef(mp);
  mpRef.current = mp;

  const send = useCallback((type: string, payload: Record<string, unknown> = {}) => {
    if (mpRef.current.session) mpRef.current.send({ type, ...payload } as never);
  }, []);
  const curId = (): string | undefined => {
    const s = stateRef.current;
    return s.currentIndex >= 0 ? s.entries[s.currentIndex]?.id : undefined;
  };

  const actions = useMemo<InitiativeActions>(() => ({
    start: (tokenIds, opts) => send('initiative:start', { tokenIds, playersRoll: opts.playersRoll, groupSame: opts.groupSame }),
    begin: () => send('initiative:begin'),
    roll: (entryId) => send('initiative:roll', { entryId }),
    setValue: (entryId, value, tokenId) => send('initiative:set-value', { entryId, value, ...(tokenId ? { tokenId } : {}) }),
    add: (tokenIds, joinGroups) => send('initiative:add', { tokenIds, joinGroups }),
    remove: (tokenIds) => send('initiative:remove', { tokenIds }),
    move: (entryId, beforeId) => send('initiative:move', { entryId, beforeId }),
    next: () => { const id = curId(); send('initiative:next', id ? { entryId: id } : {}); },
    prev: () => { const id = curId(); send('initiative:prev', id ? { entryId: id } : {}); },
    delay: () => { const id = curId(); send('initiative:delay', id ? { entryId: id } : {}); },
    join: (entryId) => send('initiative:join', { entryId }),
    split: (entryId) => send('initiative:split', { entryId }),
    defeat: (tokenId, out) => send('initiative:defeat', { tokenId, out }),
    dismissNew: (tokenIds) => send('initiative:dismiss-new', { tokenIds }),
    end: () => send('initiative:end'),
    undoLast: (undoId) => send('initiative:undo', { undoId }),
  }), [send]);

  const value = useMemo(() => ({ initiative: { ...initiative, ...actions } }), [initiative, actions]);

  return (
    <InitiativeContext.Provider value={value}>
      {children}
    </InitiativeContext.Provider>
  );
}

export function useInitiativeContext() {
  const ctx = useContext(InitiativeContext);
  if (!ctx) throw new Error('useInitiativeContext must be used within InitiativeProvider');
  return ctx;
}

/** The same, or null outside a provider (a canvas that is also drawn in Studio, a test without the provider). */
export function useOptionalInitiativeContext(): InitiativeContextValue | null {
  return useContext(InitiativeContext);
}
